import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavBar from "../components/AdminNavBar";
import { AdminPageBody, AdminLoadingState, AdminEmptyState } from "../components/AdminPageShell";
import { useAdminLearningPaths } from "../store/admin";
import Toast from "../components/Toast";
import { htmlToText } from "../utils/toDisplayString";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  BookOpen,
  Route,
  Tag,
  Wallet,
  Layers,
  Calendar,
  User,
} from "lucide-react";

/** Collapse API status values into the same buckets used by the list page. */
function mapStatus(raw) {
  const s = String(raw || "PENDING").toUpperCase();
  if (s === "DRAFT") return "Draft";
  if (["PENDING", "PENDING_REVIEW", "UNDER_REVIEW"].includes(s)) return "Pending";
  if (["REJECTED", "DISABLED", "ARCHIVED", "SUSPENDED", "WITHDRAWN"].includes(s)) return "Archived";
  if (["APPROVED", "ACTIVE", "PUBLISHED"].includes(s)) return "Active";
  return s.charAt(0) + s.slice(1).toLowerCase();
}

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Pending: "bg-[#dae2fd] text-[#565e74]",
  Draft: "bg-slate-100 text-slate-600",
  Archived: "bg-[#e0e3e5] text-[#434655]",
};

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isNaN(n) || value == null
    ? "Free"
    : `₦${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

/** Pull the bundled courses out of whichever shape the API returns. */
function extractCourses(lp) {
  const list = lp?.courses || lp?.items || lp?.pathCourses || [];
  if (!Array.isArray(list)) return [];
  return list.map((entry) => {
    const course = entry?.course || entry;
    return {
      key: course.id || entry?.id,
      title: course.title || course.name || "Untitled course",
      author: course.creatorName || course.instructorName || "—",
      price: course.price != null ? formatMoney(course.price) : "—",
    };
  });
}


// ===== LEARNING PATH DETAIL — GET /api/admin/learning-paths/:id =====
// Full review of a single learning path before the admin approves or rejects.
const LearningPathDetail = () => {
  const { learningPathId } = useParams();
  const navigate = useNavigate();

  const {
    learningPathDetail,
    learningPathDetailLoading,
    learningPathDetailError,
    fetchLearningPathById,
    updateLearningPath,
    approveLearningPath,
    rejectLearningPath,
    learningPathMutationLoading,
    learningPathMutationError,
  } = useAdminLearningPaths();

  const [loaded, setLoaded] = useState(false);
  const [actionError, setActionError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [toastSuccess, setToastSuccess] = useState(true);

  // GET /api/admin/learning-paths/:id
  useEffect(() => {
    if (learningPathId) {
      fetchLearningPathById(learningPathId).finally(() => setLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathId]);

  const goBack = () => navigate("/admin/learning-paths");

  const detail = learningPathDetail;
  const status = detail ? mapStatus(detail.status) : "Pending";
  const courses = detail ? extractCourses(detail) : [];
  const cover = detail ? detail.coverImage || detail.image || detail.coverImageUrl || detail.thumbnail || null : null;

  // Concise header facts (rendered as chips under the cover).
  const headerChips = detail
    ? [
        { icon: Layers, label: "Courses", value: courses.length || detail.courseCount || 0 },
        { icon: Tag, label: "Category", value: detail.category?.name || detail.categoryName || "—" },
        {
          icon: User,
          label: "Creator",
          value: detail.createdBy?.name || detail.creatorName || detail.tutor?.name || "—",
        },
        { icon: Wallet, label: "Price", value: formatMoney(detail.price) },
        { icon: Calendar, label: "Created", value: formatDate(detail.createdAt || detail.uploadedAt) },
      ]
    : [];

  const detailRows = detail
    ? [
        ["Status", status],
        ["Description", htmlToText(detail.description || detail.introduction) || "No description provided."],
        ["Enrollments", detail.enrollmentCount ?? 0],
        ["Last updated", formatDate(detail.updatedAt)],
        ...(detail.submittedAt ? [["Submitted at", formatDate(detail.submittedAt)]] : []),
        ...(detail.rejectionReason ? [["Rejection reason", detail.rejectionReason]] : []),
      ]
    : [];

  const showToast = (message, success = true) => {
    setToast(message);
    setToastSuccess(success);
    window.setTimeout(() => setToast(null), 5000);
  };

  const runAction = async (action, successMessage) => {
    setActionError("");
    try {
      const result = await action();
      if (!result?.success) {
        const message = result?.error || "Action failed";
        setActionError(message);
        showToast(message, false);
        return false;
      }
      fetchLearningPathById(learningPathId);
      showToast(successMessage, true);
      return true;
    } catch (error) {
      const message = error?.message || "Action failed";
      setActionError(message);
      showToast(message, false);
      return false;
    }
  };

  const handleApprove = () =>
    runAction(() => approveLearningPath(learningPathId), "Learning path approved");

  const submitReject = async (event) => {
    event.preventDefault();
    const ok = await runAction(
      () => rejectLearningPath(learningPathId, reason.trim()),
      "Learning path rejected"
    );
    if (ok) {
      setRejecting(false);
      setReason("");
    }
  };

  const startEditing = () => {
    if (!detail) return;
    setEditForm({
      title: detail.title || "",
      description: detail.description || "",
      coverImage: detail.coverImage || "",
      price: detail.price ?? "",
    });
    setEditing(true);
  };

  const saveEdits = async (event) => {
    event.preventDefault();
    const payload = {};
    if (editForm.title?.trim()) payload.title = editForm.title.trim();
    if (editForm.description?.trim()) payload.description = editForm.description.trim();
    if (editForm.coverImage?.trim()) payload.coverImage = editForm.coverImage.trim();
    if (editForm.price !== "" && !Number.isNaN(Number(editForm.price))) {
      payload.price = Number(editForm.price);
    }
    setEditSaving(true);
    const result = await updateLearningPath(learningPathId, payload);
    setEditSaving(false);
    if (!result?.success) {
      setActionError(result?.error || "Could not update this learning path.");
      return;
    }
    setEditing(false);
    await fetchLearningPathById(learningPathId);
  };

  const errorMessage = actionError || learningPathMutationError || learningPathDetailError;

  return (
    <div>
      <AdminNavBar
        title="Learning Paths"
        subTitle={detail?.title || "Details"}
        context="Review the full learning path before approving or rejecting it."
      />
      <AdminPageBody>
        {toast && (
          <Toast message={toast} isSuccess={toastSuccess} onClose={() => setToast(null)} />
        )}
        <div>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Learning Paths
          </button>
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {errorMessage}
          </div>
        )}

        {learningPathDetailLoading || (!loaded && !learningPathDetailError) ? (
          <AdminLoadingState />
        ) : learningPathDetailError && !detail ? (
          <AdminEmptyState title="Learning path not found" description={learningPathDetailError} />
        ) : detail ? (
          <>
            {/* ===== COVER BANNER ===== */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
              {cover ? (
                <img
                  src={cover}
                  alt={detail.title}
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-64 sm:h-80 lg:h-96 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-200 to-slate-100">
                  <Route className="w-16 h-16 text-slate-400" />
                  <span className="text-sm text-slate-500">No cover image provided</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent pointer-events-none" />

              {/* Title + status pinned to the bottom of the cover */}
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      <Route className="w-3.5 h-3.5" /> Learning Path
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${
                        statusStyles[status] || statusStyles.Pending
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white drop-shadow">
                    {detail.title || detail.name || "Untitled"}
                  </h1>
                </div>
                <div className="text-white text-right">
                  <p className="text-xs uppercase tracking-wider opacity-80">Courses</p>
                  <p className="text-2xl font-bold">{courses.length}</p>
                </div>
              </div>
            </div>

            {/* ===== DECISION BAR ===== */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Moderation decision</h2>
                  <p className="text-sm text-slate-500">
                    {status === "Draft"
                      ? "This learning path is still a draft. It must be submitted for review before it can be approved."
                      : status === "Pending"
                      ? "Approve this learning path to publish it to learners, or reject it with a reason."
                      : status === "Active"
                        ? "This learning path has already been approved. Approval and rejection are mutually exclusive, so it can no longer be rejected."
                        : "This learning path has already been rejected. Approval and rejection are mutually exclusive, so it can no longer be approved."}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={editing ? () => setEditing(false) : startEditing}
                    disabled={editSaving}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    {editing ? "Cancel edit" : "Edit details"}
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={learningPathMutationLoading || status !== "Pending"}
                    title={status !== "Pending" ? "Only pending learning paths can be approved." : undefined}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-green-600 bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejecting((v) => !v)}
                    disabled={learningPathMutationLoading || status !== "Pending"}
                    title={status !== "Pending" ? "Only pending learning paths can be rejected." : undefined}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-red-600 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>

              {rejecting && (
                <form onSubmit={submitReject} className="mt-5 border-t border-slate-100 pt-5">
                  <label htmlFor="reject-reason" className="block text-sm font-semibold text-slate-700 mb-1">
                    Reason for rejection
                  </label>
                  <textarea
                    id="reject-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. One bundled course is still pending review…"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none"
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={learningPathMutationLoading || status !== "Pending" || !reason.trim()}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-red-600 bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm rejection
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting(false)}
                      className="inline-flex min-h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {editing ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="m-0 text-base font-semibold text-slate-900">Edit details</h2>
                <p className="m-0 mt-1 text-sm text-slate-500">
                  Update the learning path details shown to learners.
                </p>
                <form onSubmit={saveEdits} className="mt-4 grid gap-3 sm:grid-cols-2">
                  <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
                    Title
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#004ac6] focus:ring-2 focus:ring-blue-100"
                      value={editForm.title || ""}
                      onChange={(event) => setEditForm((current) => ({ ...current, title: event.target.value }))}
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600 sm:col-span-2">
                    Description
                    <textarea
                      rows={4}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#004ac6] focus:ring-2 focus:ring-blue-100"
                      value={editForm.description || ""}
                      onChange={(event) => setEditForm((current) => ({ ...current, description: event.target.value }))}
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Cover image URL
                    <input
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#004ac6] focus:ring-2 focus:ring-blue-100"
                      value={editForm.coverImage || ""}
                      onChange={(event) => setEditForm((current) => ({ ...current, coverImage: event.target.value }))}
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Price
                    <input
                      type="number"
                      min="0"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#004ac6] focus:ring-2 focus:ring-blue-100"
                      value={editForm.price ?? ""}
                      onChange={(event) => setEditForm((current) => ({ ...current, price: event.target.value }))}
                    />
                  </label>
                  <div className="flex justify-end gap-2 sm:col-span-2">
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      className="inline-flex min-h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={editSaving}
                      className="inline-flex min-h-10 items-center justify-center rounded-lg bg-[#004ac6] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#003ba0] disabled:opacity-50"
                    >
                      {editSaving ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </form>
              </div>
            ) : null}

            {/* ===== QUICK FACTS ===== */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {headerChips.map((chip) => (
                <div key={chip.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <chip.icon className="w-4 h-4" />
                    <span className="text-[11px] uppercase tracking-wider font-semibold">{chip.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 truncate">{chip.value}</p>
                </div>
              ))}
            </div>

            {/* ===== DETAILS ===== */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Route className="w-4 h-4 text-slate-400" />
                <h3 className="text-base font-semibold text-slate-900">Details</h3>
              </div>
              <dl className="divide-y divide-slate-100">
                {detailRows.map(([label, value]) => (
                  <div key={label} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-3">
                    <dt className="text-sm font-medium text-slate-500">{label}</dt>
                    <dd className="sm:col-span-2 text-sm text-slate-900">
                      {label === "Status" ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            statusStyles[status] || statusStyles.Pending
                          }`}
                        >
                          {value}
                        </span>
                      ) : (
                        value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* ===== BUNDLED COURSES ===== */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  <h3 className="text-base font-semibold text-slate-900">Bundled courses</h3>
                </div>
                <span className="text-xs font-semibold text-slate-500">{courses.length} course(s)</span>
              </div>
              {courses.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate-500">
                  No courses are bundled into this learning path.
                </div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="bg-slate-50 text-left text-[12px] uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3 font-semibold">Course</th>
                      <th className="px-5 py-3 font-semibold">Author</th>
                      <th className="px-5 py-3 font-semibold text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((c) => (
                      <tr key={c.key} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-5 py-3">
                          <span className="inline-flex items-center gap-2 font-medium text-slate-900">
                            <BookOpen className="w-4 h-4 text-slate-400" />
                            {c.title}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-slate-700">{c.author}</td>
                        <td className="px-5 py-3 text-right font-['JetBrains_Mono',monospace] text-slate-900">
                          {c.price}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#004ac6] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#003ba0]"
              >
                Done — back to queue
              </button>
            </div>
          </>
        ) : null}
      </AdminPageBody>
    </div>
  );
};

export default LearningPathDetail;


